/**
 * Pure helpers for the prepaid electricity kWh display.
 *
 * Rate DATA now lives in server/data/site-rates.json and is loaded at runtime
 * via SiteRatesContext. This module holds only pure functions operating on a
 * site config object of the shape:
 *   {
 *     displayName,
 *     currentRates: { peak, offPeak },
 *     peakHours,               // carried metadata; not rendered
 *     rateHistory: [{ effectiveFrom, peak, offPeak }]
 *   }
 */

/**
 * Look up a site's config from the loaded rates map (case-insensitive).
 * @param {Object} sitesData - Map of lowercase siteName -> config
 * @param {string} siteName - The resident's site name
 * @returns {Object|null} The site config, or null if the site has no kWh config
 */
export const getSiteConfigFromData = (sitesData, siteName) => {
  if (!sitesData || typeof siteName !== 'string') return null;
  return sitesData[siteName.toLowerCase()] || null;
};

/**
 * Convert a Rand amount to kWh using the site's per-kWh rate.
 * @param {number} rands
 * @param {number} peakRate - R/kWh (pass config.currentRates.peak)
 * @returns {number} Units in kWh
 */
export const convertRandsToUnits = (rands, peakRate) => {
  if (typeof rands !== 'number' || isNaN(rands)) return 0;
  if (typeof peakRate !== 'number' || peakRate <= 0) return 0;
  return rands / peakRate;
};

/**
 * Get the applicable rate for a specific date from a site's rate history.
 * @param {string|Date} date
 * @param {Object} siteConfig
 * @returns {Object} { peak, offPeak }
 */
export const getRateForDate = (date, siteConfig) => {
  if (!siteConfig) return { peak: 0, offPeak: 0 };
  if (!date || !siteConfig.rateHistory?.length) {
    return siteConfig.currentRates;
  }

  const targetDate = new Date(date);
  if (isNaN(targetDate.getTime())) {
    return siteConfig.currentRates;
  }

  // Most-recent effective rate that is not after the target date.
  const sortedHistory = [...siteConfig.rateHistory].sort(
    (a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom)
  );
  for (const rate of sortedHistory) {
    if (targetDate >= new Date(rate.effectiveFrom)) {
      return { peak: rate.peak, offPeak: rate.offPeak };
    }
  }

  // Target predates all history — use the oldest known rate.
  const oldest = sortedHistory[sortedHistory.length - 1];
  return { peak: oldest.peak, offPeak: oldest.offPeak };
};

/**
 * Format kWh units for display.
 * @param {number} units
 * @returns {string}
 */
export const formatUnits = (units) => {
  if (typeof units !== 'number' || isNaN(units)) return '0.00';
  return units.toFixed(2);
};

/**
 * True when a site config has a usable pair of numeric rates. Guards against a
 * structurally-incomplete entry in the runtime rate file (a non-dev typo that
 * omits offPeak or quotes the numbers) so consumers degrade to Rands-only
 * instead of throwing during render.
 * @param {Object} siteConfig
 * @returns {boolean}
 */
export const hasValidRates = (siteConfig) => {
  const rates = siteConfig?.currentRates;
  return typeof rates?.peak === 'number' && typeof rates?.offPeak === 'number';
};

/**
 * True when peak and off-peak rates are identical (a single all-day flat rate).
 * @param {Object} rates - { peak, offPeak } (pass config.currentRates)
 * @returns {boolean}
 */
export const isFlatRate = (rates) => {
  return !!rates && rates.peak === rates.offPeak;
};
